import React, { useMemo } from 'react';
import { Freeze, Sequence, useCurrentFrame } from 'remotion';
import { EditorStarterItem } from '../items/item-type';
import { TransitionItem } from '../items/transition/transition-item-type';
import { TransitionExtensionContext } from './transition-extension-context';

interface FrozenFrameWrapperProps {
  item: EditorStarterItem;
  // 현재 아이템 뒤의 트랜지션 (source로서의 frozen frames 적용)
  nextTransition: TransitionItem | undefined;
  // 현재 아이템 앞의 트랜지션 (target으로서의 frozen frames 적용)
  prevTransition: TransitionItem | undefined;
  // 현재 아이템이 source로서 뒤로 확장된 프레임 수
  sourceExtension?: number;
  // 현재 아이템이 target으로서 앞으로 확장된 프레임 수
  targetExtension?: number;
  children: React.ReactNode;
}

/**
 * 트랜지션 영역에서 프레임 매핑을 처리하는 래퍼 컴포넌트
 *
 * TransitionSeries 내에서 클립의 effectiveDuration이 item.durationInFrames보다 길 때,
 * 비디오/오디오 프레임을 올바르게 매핑합니다.
 *
 * effectiveDuration = item.durationInFrames + prevTransHalf + nextTransHalf + sourceExtension + targetExtension
 *
 * 프레임 구간:
 * 1. [0, frozenBefore - 1]: 첫 프레임 freeze
 * 2. [frozenBefore, frozenBefore + totalPlayable - 1]: 영상 재생 (확장 포함)
 * 3. [이후]: 마지막 프레임 freeze
 *
 * 확장 정보가 있으면 freeze 대신 실제 확장된 영상을 재생합니다.
 */
export const FrozenFrameWrapper: React.FC<FrozenFrameWrapperProps> = ({
  item,
  nextTransition: _nextTransition,
  prevTransition,
  sourceExtension = 0,
  targetExtension = 0,
  children,
}) => {
  const frame = useCurrentFrame();

  // 앞쪽 트랜지션 절반 계산
  const prevTransHalf = prevTransition
    ? Math.ceil(prevTransition.durationInFrames / 2)
    : 0;

  // 앞쪽 freeze가 필요한 프레임 수 계산
  // 확장 정보가 트랜지션 절반보다 작으면 나머지는 freeze
  const frozenBefore = Math.max(0, prevTransHalf - targetExtension);

  // 실제 재생 가능한 총 프레임 수 (확장 포함)
  const totalPlayable =
    targetExtension + item.durationInFrames + sourceExtension;

  // 영상 재생 시작 프레임 (freeze 이후)
  const playbackStart = frozenBefore;
  // 영상 재생 종료 프레임
  const playbackEnd = playbackStart + totalPlayable;

  // 확장 영역 판단: 현재 프레임이 확장된 부분에서 재생 중인지 확인
  // 재생 구간 내에서:
  // - [playbackStart, playbackStart + targetExtension - 1]: target 확장 영역 (앞으로 확장)
  // - [playbackEnd - sourceExtension, playbackEnd - 1]: source 확장 영역 (뒤로 확장)
  const isInExtensionArea = useMemo(() => {
    // freeze 구간은 확장 영역이 아님
    if (frame < playbackStart || frame >= playbackEnd) {
      return false;
    }

    // 재생 구간 내에서 확장 영역인지 확인
    const playbackFrame = frame - playbackStart;

    // target 확장 영역 (앞으로 확장된 부분)
    if (targetExtension > 0 && playbackFrame < targetExtension) {
      return true;
    }

    // source 확장 영역 (뒤로 확장된 부분)
    if (
      sourceExtension > 0 &&
      playbackFrame >= targetExtension + item.durationInFrames
    ) {
      return true;
    }

    return false;
  }, [
    frame,
    playbackStart,
    playbackEnd,
    targetExtension,
    sourceExtension,
    item.durationInFrames,
  ]);

  // target 확장으로 인한 assetStart 조정값 (prevTransition에서 가져옴)
  const assetStartDeltaInSeconds =
    prevTransition?.targetExtendedAssetStartDelta ?? 0;

  const contextValue = useMemo(
    () => ({ isInExtensionArea, assetStartDeltaInSeconds }),
    [isInExtensionArea, assetStartDeltaInSeconds],
  );

  // 앞쪽 freeze 구간
  if (frame < playbackStart) {
    return (
      <TransitionExtensionContext.Provider value={contextValue}>
        <Freeze frame={0}>{children}</Freeze>
      </TransitionExtensionContext.Provider>
    );
  }

  // 뒤쪽 freeze 구간
  if (frame >= playbackEnd) {
    return (
      <TransitionExtensionContext.Provider value={contextValue}>
        <Freeze frame={item.durationInFrames + sourceExtension - 1}>
          {children}
        </Freeze>
      </TransitionExtensionContext.Provider>
    );
  }

  // 재생 구간: 프레임 오프셋 적용
  // playbackStart에서 재생이 시작되고, 자식의 frame은 0부터 시작
  // targetExtension만큼 앞으로 확장된 영상은 assetStartDelta로 처리됨 (VideoLayer/AudioLayer에서)
  // Sequence from을 사용하면 자식의 useCurrentFrame()이 조정됨
  // 자식의 useCurrentFrame() = frame - from
  const sequenceFrom = playbackStart;

  if (sequenceFrom === 0) {
    return (
      <TransitionExtensionContext.Provider value={contextValue}>
        {children}
      </TransitionExtensionContext.Provider>
    );
  }

  return (
    <TransitionExtensionContext.Provider value={contextValue}>
      <Sequence
        from={sequenceFrom}
        layout="none"
      >
        {children}
      </Sequence>
    </TransitionExtensionContext.Provider>
  );
};
